import { inputBaseClasses } from '@mui/material';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { JobState } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { debounce } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';

import ToggleSidebarButton from '~components/Buttons/ToggleSidebarButton';
import { ACTIVE_JOB_STATES, DONE_JOB_STATES } from '~components/Maps/v2/constants';
import { SearchBar } from '~components/Search/SearchBar';
import Tabs from '~components/Tabs/Tabs';
import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';
import { FeatureFlags } from '~constants/featureFlags';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import { useStores } from '~store';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

import LiveMapFilters from './SideBarComponents/LiveMapFilters';
import { LiveMapTabFilter } from './types';

const VIEW_TO_JOB_STATES: Partial<Record<LiveMapTabFilter, JobState[]>> = {
  [LiveMapTabFilter.IN_PROGRESS]: ACTIVE_JOB_STATES,
  [LiveMapTabFilter.HISTORY]: DONE_JOB_STATES,
};

const availableViews = [
  {
    label: t('live_map.filters.in_progress'),
    value: LiveMapTabFilter.IN_PROGRESS,
  },
  {
    label: t('live_map.filters.history'),
    value: LiveMapTabFilter.HISTORY,
  },
  {
    label: t('live_map.filters.geofence_reports'),
    value: LiveMapTabFilter.GEOFENCE_REPORTS,
  },
];

interface TreadLiveTopBarProps {
  isSidebarCollapsed?: boolean;
  onTabChange?: (tab: LiveMapTabFilter) => void;
}

const TreadLiveTopBar = ({ isSidebarCollapsed, onTabChange }: TreadLiveTopBarProps) => {
  const theme = useTheme();
  const [view, setViewFilter] = useState(LiveMapTabFilter.IN_PROGRESS);
  const { liveMapStoreNew, userStore } = useStores();
  const companyId = userStore.currentCompanies[0]?.id || userStore.userCompany?.id;

  const isGeofenceReportsEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.geofenceReportsEnabled,
  });

  const tabs = useMemo(() => {
    return availableViews.filter((tab) => {
      if (tab.value === LiveMapTabFilter.GEOFENCE_REPORTS) {
        return isGeofenceReportsEnabled;
      }
      return true;
    });
  }, [isGeofenceReportsEnabled]);

  /**
   * Persist the view filter on mount given by persisted filters
   */
  useEffect(() => {
    const firstJobState = liveMapStoreNew?.filters?.jobStates?.[0];
    const jobStatesForView = VIEW_TO_JOB_STATES[view];
    const isJobStateInView = firstJobState && jobStatesForView?.includes(firstJobState);

    if (!isJobStateInView) {
      setViewFilter(LiveMapTabFilter.HISTORY);
    }
  }, []);

  const onViewChange = (view: LiveMapTabFilter) => {
    setViewFilter(view);
    onTabChange?.(view);

    const jobStates = VIEW_TO_JOB_STATES[view];
    if (jobStates) {
      liveMapStoreNew.resetOrders({
        ...liveMapStoreNew.filters,
        jobStates,
      });
    }
  };

  const applySearchFilter = debounce((value: string) => {
    if (liveMapStoreNew.filters.search !== value) {
      liveMapStoreNew.setFilters({ search: value }, true);
    }
  }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS);

  return (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: 'background.paper',
        borderBottom: isSidebarCollapsed
          ? 'none'
          : `solid 1px ${theme.brandV2.colors.treadGray7}`,
        py: 1,
        px: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        {!isSidebarCollapsed && (
          <Tabs
            onChange={onViewChange}
            selected={view}
            tabs={tabs}
            sx={{
              width: '100%',
              mb: 1,
            }}
          />
        )}

        <ToggleSidebarButton
          isCollapsed={Boolean(isSidebarCollapsed)}
          onClick={() => liveMapStoreNew.toggleSidebar()}
          sx={{ ml: isSidebarCollapsed ? -0.5 : 1, mt: '3px' }}
        />
      </Box>
      {!isSidebarCollapsed && view !== LiveMapTabFilter.GEOFENCE_REPORTS && (
        <Box
          sx={{
            display: 'grid',
            gap: 1,
            gridAutoColumns: '1fr auto',
            gridTemplateAreas: "'dateSelector dateSelector' 'searchBar filters'",
          }}
        >
          <LiveMapFilters />

          <Box sx={{ gridArea: 'searchBar' }}>
            <SearchBar
              key={companyId}
              alwaysExpanded
              setFilter={applySearchFilter}
              placeHolder={
                t('common.search_by', {
                  field: `${t('common.account')}, ${t('common.order_id')}, ${t('common.or')} ${t('common.project_name')}`,
                }) as string
              }
              inputSx={{
                [`&.${inputBaseClasses.root}`]: {
                  height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
                },
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default observer(TreadLiveTopBar);
